/**
 * Spearman (枪兵) - Blue heavy armor, tall helmet, long spear with red tassel
 * Heavy infantry with reach advantage
 *
 * 坐标系统说明 (Y轴向上):
 * - 脚底: Y = 0
 * - 头顶: Y ≈ 0.70
 * - 身体中心: X = 0, Z = 0
 * - 枪杆位置: 右侧，双手握持
 */

import * as THREE from 'three';
import { COLORS } from '../unitConstants';
import { mergeGeometries } from '../unitHelpers';

export function createSpearmanGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const colors: THREE.Color[] = [];

  // ==================== 头部 ====================
  const head = new THREE.SphereGeometry(0.08, 10, 8);
  head.translate(0, 0.52, 0);
  parts.push(head);
  colors.push(COLORS.skin);

  // ==================== 头盔（中式尖顶盔） ====================
  // 头盔主体（半球形）
  const helmetDome = new THREE.SphereGeometry(
    0.09,
    8,
    6,
    0,
    Math.PI * 2,
    0,
    Math.PI * 0.55
  );
  helmetDome.translate(0, 0.54, 0);
  parts.push(helmetDome);
  colors.push(COLORS.spearBlue);

  // 头盔金边
  const helmetRim = new THREE.TorusGeometry(0.085, 0.01, 4, 12);
  helmetRim.rotateX(Math.PI / 2);
  helmetRim.translate(0, 0.54, 0);
  parts.push(helmetRim);
  colors.push(COLORS.gold);

  // 额头装饰板
  const foreheadPlate = new THREE.BoxGeometry(0.06, 0.025, 0.012);
  foreheadPlate.translate(0, 0.52, 0.075);
  parts.push(foreheadPlate);
  colors.push(COLORS.gold);

  // 头盔顶部尖刺（更高）
  const spike = new THREE.ConeGeometry(0.018, 0.08, 6);
  spike.translate(0, 0.68, 0);
  parts.push(spike);
  colors.push(COLORS.gold);

  // 尖刺底座
  const spikeBase = new THREE.CylinderGeometry(0.022, 0.018, 0.015, 6);
  spikeBase.translate(0, 0.635, 0);
  parts.push(spikeBase);
  colors.push(COLORS.gold);

  // 护颈
  const neckGuard = new THREE.CylinderGeometry(
    0.085,
    0.095,
    0.035,
    8,
    1,
    true,
    Math.PI * 0.25,
    Math.PI * 1.5
  );
  neckGuard.translate(0, 0.50, 0);
  parts.push(neckGuard);
  colors.push(COLORS.spearBlue);

  // ==================== 躯干（重甲） ====================
  // 上身铠甲
  const torso = new THREE.CylinderGeometry(0.075, 0.085, 0.16, 8);
  torso.translate(0, 0.36, 0);
  parts.push(torso);
  colors.push(COLORS.spearBlue);

  // 胸甲装饰（护心镜）
  const chestPlate = new THREE.CylinderGeometry(0.035, 0.035, 0.012, 8);
  chestPlate.rotateX(Math.PI / 2);
  chestPlate.translate(0, 0.38, 0.078);
  parts.push(chestPlate);
  colors.push(COLORS.bronze);

  // 腰带
  const belt = new THREE.CylinderGeometry(0.088, 0.088, 0.022, 8);
  belt.translate(0, 0.27, 0);
  parts.push(belt);
  colors.push(COLORS.gold);

  // 腰带扣
  const buckle = new THREE.BoxGeometry(0.028, 0.022, 0.012);
  buckle.translate(0, 0.27, 0.085);
  parts.push(buckle);
  colors.push(COLORS.gold);

  // 肩甲
  const leftShoulder = new THREE.BoxGeometry(0.05, 0.02, 0.06);
  leftShoulder.translate(-0.095, 0.42, 0);
  parts.push(leftShoulder);
  colors.push(COLORS.spearBlue);

  const rightShoulder = new THREE.BoxGeometry(0.05, 0.02, 0.06);
  rightShoulder.translate(0.095, 0.42, 0);
  parts.push(rightShoulder);
  colors.push(COLORS.spearBlue);

  // 肩甲金边
  const leftShoulderEdge = new THREE.BoxGeometry(0.055, 0.008, 0.065);
  leftShoulderEdge.translate(-0.095, 0.435, 0);
  parts.push(leftShoulderEdge);
  colors.push(COLORS.gold);

  const rightShoulderEdge = new THREE.BoxGeometry(0.055, 0.008, 0.065);
  rightShoulderEdge.translate(0.095, 0.435, 0);
  parts.push(rightShoulderEdge);
  colors.push(COLORS.gold);

  // 下摆/裙甲
  const skirt = new THREE.CylinderGeometry(0.085, 0.11, 0.09, 8);
  skirt.translate(0, 0.195, 0);
  parts.push(skirt);
  colors.push(COLORS.spearBlue);

  // 前甲片
  for (let i = -1; i <= 1; i++) {
    const plate = new THREE.BoxGeometry(0.035, 0.06, 0.012);
    plate.translate(i * 0.04, 0.16, 0.10);
    parts.push(plate);
    colors.push(COLORS.spearBlue);
  }

  // ==================== 枪杆位置定义 ====================
  // 枪杆在身体右前方
  const spearX = 0.12;  // 枪杆X位置
  const spearZ = 0.05;  // 枪杆Z位置（向前）

  // ==================== 左臂（握枪下方，更明显向外伸展） ====================
  // 左手目标位置（握住枪杆下方）
  const leftHandX = spearX;
  const leftHandY = 0.30;
  const leftHandZ = spearZ;

  // 左肩膀位置
  const leftShoulderX = -0.10;
  const leftShoulderY = 0.40;
  const leftShoulderZ = 0;

  // 左肘位置（在肩膀和手之间，向外弯曲使手臂可见）
  const leftElbowX = -0.02;
  const leftElbowY = 0.32;
  const leftElbowZ = 0.08;

  // 左上臂（从肩膀到肘部）
  const leftUpperArmDx = leftElbowX - leftShoulderX;
  const leftUpperArmDy = leftElbowY - leftShoulderY;
  const leftUpperArmDz = leftElbowZ - leftShoulderZ;
  const leftUpperArmLength = Math.sqrt(leftUpperArmDx * leftUpperArmDx + leftUpperArmDy * leftUpperArmDy + leftUpperArmDz * leftUpperArmDz);
  const leftUpperArm = new THREE.CylinderGeometry(0.028, 0.025, leftUpperArmLength, 6);
  // 计算旋转角度
  const leftUpperArmAngleZ = Math.atan2(leftUpperArmDx, -leftUpperArmDy);
  const leftUpperArmAngleX = Math.atan2(leftUpperArmDz, Math.sqrt(leftUpperArmDx * leftUpperArmDx + leftUpperArmDy * leftUpperArmDy));
  leftUpperArm.rotateZ(leftUpperArmAngleZ);
  leftUpperArm.rotateX(leftUpperArmAngleX);
  leftUpperArm.translate(
    (leftShoulderX + leftElbowX) / 2,
    (leftShoulderY + leftElbowY) / 2,
    (leftShoulderZ + leftElbowZ) / 2
  );
  parts.push(leftUpperArm);
  colors.push(COLORS.spearBlue);

  // 左前臂（从肘部到手）
  const leftForearmDx = leftHandX - leftElbowX;
  const leftForearmDy = leftHandY - leftElbowY;
  const leftForearmDz = leftHandZ - leftElbowZ;
  const leftForearmLength = Math.sqrt(leftForearmDx * leftForearmDx + leftForearmDy * leftForearmDy + leftForearmDz * leftForearmDz);
  const leftForearm = new THREE.CylinderGeometry(0.025, 0.020, leftForearmLength, 6);
  const leftForearmAngleZ = Math.atan2(leftForearmDx, -leftForearmDy);
  const leftForearmAngleX = Math.atan2(leftForearmDz, Math.sqrt(leftForearmDx * leftForearmDx + leftForearmDy * leftForearmDy));
  leftForearm.rotateZ(leftForearmAngleZ);
  leftForearm.rotateX(leftForearmAngleX);
  leftForearm.translate(
    (leftElbowX + leftHandX) / 2,
    (leftElbowY + leftHandY) / 2,
    (leftElbowZ + leftHandZ) / 2
  );
  parts.push(leftForearm);
  colors.push(COLORS.spearBlue);

  // 左肘关节（圆球）
  const leftElbow = new THREE.SphereGeometry(0.026, 6, 5);
  leftElbow.translate(leftElbowX, leftElbowY, leftElbowZ);
  parts.push(leftElbow);
  colors.push(COLORS.spearBlue);

  // 左手（握住枪杆）
  const leftHand = new THREE.SphereGeometry(0.024, 6, 5);
  leftHand.translate(leftHandX, leftHandY, leftHandZ);
  parts.push(leftHand);
  colors.push(COLORS.skin);

  // 左手握拳效果（手指环绕枪杆）
  const leftGrip = new THREE.TorusGeometry(0.022, 0.010, 6, 8);
  leftGrip.rotateX(Math.PI / 2);
  leftGrip.translate(leftHandX, leftHandY, leftHandZ);
  parts.push(leftGrip);
  colors.push(COLORS.skin);

  // ==================== 右臂（握枪上方，手在枪杆前面） ====================
  // 右手目标位置（握住枪杆上方）
  const rightHandX = spearX;
  const rightHandY = 0.42;
  const rightHandZ = spearZ;

  // 右肩膀位置
  const rightShoulderX = 0.10;
  const rightShoulderY = 0.40;
  const rightShoulderZ = 0;

  // 右肘位置（向前向外弯曲）
  const rightElbowX = 0.14;
  const rightElbowY = 0.38;
  const rightElbowZ = 0.08;

  // 右上臂（从肩膀到肘部）
  const rightUpperArmDx = rightElbowX - rightShoulderX;
  const rightUpperArmDy = rightElbowY - rightShoulderY;
  const rightUpperArmDz = rightElbowZ - rightShoulderZ;
  const rightUpperArmLength = Math.sqrt(rightUpperArmDx * rightUpperArmDx + rightUpperArmDy * rightUpperArmDy + rightUpperArmDz * rightUpperArmDz);
  const rightUpperArm = new THREE.CylinderGeometry(0.028, 0.025, rightUpperArmLength, 6);
  const rightUpperArmAngleZ = Math.atan2(rightUpperArmDx, -rightUpperArmDy);
  const rightUpperArmAngleX = Math.atan2(rightUpperArmDz, Math.sqrt(rightUpperArmDx * rightUpperArmDx + rightUpperArmDy * rightUpperArmDy));
  rightUpperArm.rotateZ(rightUpperArmAngleZ);
  rightUpperArm.rotateX(rightUpperArmAngleX);
  rightUpperArm.translate(
    (rightShoulderX + rightElbowX) / 2,
    (rightShoulderY + rightElbowY) / 2,
    (rightShoulderZ + rightElbowZ) / 2
  );
  parts.push(rightUpperArm);
  colors.push(COLORS.spearBlue);

  // 右前臂（从肘部到手）
  const rightForearmDx = rightHandX - rightElbowX;
  const rightForearmDy = rightHandY - rightElbowY;
  const rightForearmDz = rightHandZ - rightElbowZ;
  const rightForearmLength = Math.sqrt(rightForearmDx * rightForearmDx + rightForearmDy * rightForearmDy + rightForearmDz * rightForearmDz);
  const rightForearm = new THREE.CylinderGeometry(0.025, 0.020, rightForearmLength, 6);
  const rightForearmAngleZ = Math.atan2(rightForearmDx, -rightForearmDy);
  const rightForearmAngleX = Math.atan2(rightForearmDz, Math.sqrt(rightForearmDx * rightForearmDx + rightForearmDy * rightForearmDy));
  rightForearm.rotateZ(rightForearmAngleZ);
  rightForearm.rotateX(rightForearmAngleX);
  rightForearm.translate(
    (rightElbowX + rightHandX) / 2,
    (rightElbowY + rightHandY) / 2,
    (rightElbowZ + rightHandZ) / 2
  );
  parts.push(rightForearm);
  colors.push(COLORS.spearBlue);

  // 右肘关节（圆球）
  const rightElbow = new THREE.SphereGeometry(0.026, 6, 5);
  rightElbow.translate(rightElbowX, rightElbowY, rightElbowZ);
  parts.push(rightElbow);
  colors.push(COLORS.spearBlue);

  // 右手（握住枪杆）
  const rightHand = new THREE.SphereGeometry(0.024, 6, 5);
  rightHand.translate(rightHandX, rightHandY, rightHandZ);
  parts.push(rightHand);
  colors.push(COLORS.skin);

  // 右手握拳效果（手指环绕枪杆）
  const rightGrip = new THREE.TorusGeometry(0.022, 0.010, 6, 8);
  rightGrip.rotateX(Math.PI / 2);
  rightGrip.translate(rightHandX, rightHandY, rightHandZ);
  parts.push(rightGrip);
  colors.push(COLORS.skin);

  // 护腕装饰
  const leftWristGuard = new THREE.CylinderGeometry(0.026, 0.024, 0.025, 6);
  leftWristGuard.translate(
    (leftElbowX + leftHandX) / 2 + 0.02,
    (leftElbowY + leftHandY) / 2,
    (leftElbowZ + leftHandZ) / 2
  );
  parts.push(leftWristGuard);
  colors.push(COLORS.bronze);

  const rightWristGuard = new THREE.CylinderGeometry(0.026, 0.024, 0.025, 6);
  rightWristGuard.translate(
    (rightElbowX + rightHandX) / 2,
    (rightElbowY + rightHandY) / 2,
    (rightElbowZ + rightHandZ) / 2
  );
  parts.push(rightWristGuard);
  colors.push(COLORS.bronze);

  // ==================== 腿部 ====================
  // 左大腿
  const leftThigh = new THREE.CylinderGeometry(0.035, 0.030, 0.08, 6);
  leftThigh.translate(-0.038, 0.11, 0);
  parts.push(leftThigh);
  colors.push(COLORS.spearBlue);

  // 左小腿
  const leftShin = new THREE.CylinderGeometry(0.030, 0.024, 0.07, 6);
  leftShin.translate(-0.038, 0.04, 0);
  parts.push(leftShin);
  colors.push(COLORS.spearBlue);

  // 左脚
  const leftFoot = new THREE.BoxGeometry(0.038, 0.018, 0.052);
  leftFoot.translate(-0.038, 0.009, 0.008);
  parts.push(leftFoot);
  colors.push(COLORS.iron);

  // 右大腿
  const rightThigh = new THREE.CylinderGeometry(0.035, 0.030, 0.08, 6);
  rightThigh.translate(0.038, 0.11, 0);
  parts.push(rightThigh);
  colors.push(COLORS.spearBlue);

  // 右小腿
  const rightShin = new THREE.CylinderGeometry(0.030, 0.024, 0.07, 6);
  rightShin.translate(0.038, 0.04, 0);
  parts.push(rightShin);
  colors.push(COLORS.spearBlue);

  // 右脚
  const rightFoot = new THREE.BoxGeometry(0.038, 0.018, 0.052);
  rightFoot.translate(0.038, 0.009, 0.008);
  parts.push(rightFoot);
  colors.push(COLORS.iron);

  // ==================== 武器：红缨枪 ====================
  // 枪杆（从下到上贯穿，经过双手）
  const shaftLength = 0.90;
  const shaftBottom = 0.05;  // 枪底距离地面
  const shaftCenter = shaftBottom + shaftLength / 2;
  const shaft = new THREE.CylinderGeometry(0.014, 0.018, shaftLength, 8);
  shaft.translate(spearX, shaftCenter, spearZ);
  parts.push(shaft);
  colors.push(COLORS.wood);

  // 枪杆加固环（铜色）
  for (const y of [0.20, 0.45, 0.70]) {
    const ring = new THREE.TorusGeometry(0.018, 0.005, 4, 10);
    ring.rotateX(Math.PI / 2);
    ring.translate(spearX, y, spearZ);
    parts.push(ring);
    colors.push(COLORS.bronze);
  }

  // ==================== 枪头（锋利的叶形枪尖） ====================
  const spearTipBase = shaftBottom + shaftLength;  // 枪杆顶端

  // 枪头底座/颈部（连接枪杆）
  const collar = new THREE.CylinderGeometry(0.022, 0.016, 0.025, 8);
  collar.translate(spearX, spearTipBase + 0.012, spearZ);
  parts.push(collar);
  colors.push(COLORS.bronze);

  // 主枪头 - 使用更多面数的锥形，更细更尖
  const spearHeadLength = 0.14;
  const spearHead = new THREE.ConeGeometry(0.025, spearHeadLength, 12);  // 12面，更圆滑
  spearHead.translate(spearX, spearTipBase + 0.025 + spearHeadLength / 2, spearZ);
  parts.push(spearHead);
  colors.push(COLORS.steel);

  // 枪头刃部装饰（两侧的锋刃效果）
  const bladeEdge1 = new THREE.BoxGeometry(0.003, spearHeadLength * 0.7, 0.035);
  bladeEdge1.translate(spearX, spearTipBase + 0.06, spearZ);
  parts.push(bladeEdge1);
  colors.push(new THREE.Color(0xc0c0c0));  // 亮银色刃

  const bladeEdge2 = new THREE.BoxGeometry(0.035, spearHeadLength * 0.7, 0.003);
  bladeEdge2.translate(spearX, spearTipBase + 0.06, spearZ);
  parts.push(bladeEdge2);
  colors.push(new THREE.Color(0xc0c0c0));

  // 枪尖尖端（额外的细长锥形，更锋利）
  const sharpTip = new THREE.ConeGeometry(0.008, 0.04, 8);
  sharpTip.translate(spearX, spearTipBase + 0.025 + spearHeadLength + 0.02, spearZ);
  parts.push(sharpTip);
  colors.push(new THREE.Color(0xe8e8e8));  // 闪亮的尖端

  // ==================== 红缨（标志性特征） ====================
  const tasselY = spearTipBase;  // 红缨在枪头底部

  // 红缨底座
  const tasselBase = new THREE.SphereGeometry(0.020, 8, 6);
  tasselBase.translate(spearX, tasselY, spearZ);
  parts.push(tasselBase);
  colors.push(COLORS.tassel);

  // 红缨丝缕（更多更长更飘逸）
  for (let i = 0; i < 12; i++) {
    const strandLength = 0.06 + Math.random() * 0.02;
    const strand = new THREE.CylinderGeometry(0.004, 0.002, strandLength, 4);
    const angle = (i / 12) * Math.PI * 2;
    const swayAngle = 0.5 + Math.random() * 0.2;  // 随机飘动角度
    strand.rotateZ(swayAngle);
    strand.rotateY(angle);
    strand.translate(
      spearX + Math.cos(angle) * 0.018,
      tasselY - strandLength / 2 - 0.01,
      spearZ + Math.sin(angle) * 0.018
    );
    parts.push(strand);
    colors.push(COLORS.tassel);
  }

  // 红缨丝尾端（小球装饰）
  for (let i = 0; i < 4; i++) {
    const tip = new THREE.SphereGeometry(0.006, 4, 3);
    const angle = (i / 4) * Math.PI * 2 + Math.PI / 4;
    tip.translate(
      spearX + Math.cos(angle) * 0.025,
      tasselY - 0.07,
      spearZ + Math.sin(angle) * 0.025
    );
    parts.push(tip);
    colors.push(COLORS.tassel);
  }

  // ==================== 枪尾铜帽 ====================
  const buttCap = new THREE.ConeGeometry(0.018, 0.04, 8);
  buttCap.rotateX(Math.PI);
  buttCap.translate(spearX, shaftBottom - 0.02, spearZ);
  parts.push(buttCap);
  colors.push(COLORS.bronze);

  return mergeGeometries(parts, colors);
}
